import {
  type AgingSummary,
  type LeafAgingSummary,
  LeafAgingSummaryAmountColumn,
  ReportKind,
} from "@digits-graphql/frontend/graphql-bearer"
import { type MonetaryValue } from "@digits-graphql/frontend/graphql-public"
import monetaryValueHelper from "@digits-shared/helpers/monetaryValueHelper"
import dayjs from "@digits-shared/initializers/dayjs/dayjs"
import { isAgingReportKind } from "src/frontend/components/OS/Applications/Reports/Packages/shared"
import {
  type LEAF_AGING_AMOUNT_COLUMNS,
  TIME_COLUMNS,
  TOTAL_COLUMN,
} from "src/frontend/components/Shared/Layout/Components/Statements/AgingReport/AgingColumns"
import {
  type ExcelRow,
  type ExcelRowMappingOpts,
} from "src/frontend/components/Shared/Layout/Components/Statements/Excel/useExportToExcel"

const CUSTOMER_NAME = "Customer Name" as const
const VENDOR_NAME = "Vendor Name" as const
const AP_TITLE_SUMMARY_COLUMNS = [VENDOR_NAME, ...TIME_COLUMNS, TOTAL_COLUMN]
const AR_TITLE_SUMMARY_COLUMNS = [CUSTOMER_NAME, ...TIME_COLUMNS, TOTAL_COLUMN]
const AP_SUMMARY_COLUMNS = [
  VENDOR_NAME,
  "Invoice Date",
  "Due Date",
  "Invoice Number",
  ...TIME_COLUMNS,
  TOTAL_COLUMN,
] as const

const AR_SUMMARY_COLUMNS = [
  CUSTOMER_NAME,
  "Invoice Date",
  "Due Date",
  "Invoice Number",
  ...TIME_COLUMNS,
  TOTAL_COLUMN,
] as const

export function mapAgingReportRows({ rows, config }: ExcelRowMappingOpts): ExcelRow[] {
  const { kind } = config
  if (!isAgingReportKind(kind)) {
    return []
  }

  return rows.reduce((agg, row) => {
    const { leafAgingSummary, parentAgingSummary, parentAgingTitle, agingSummary } = row
    if (leafAgingSummary) {
      agg.push(mapInvoiceSummaryColumns(kind, leafAgingSummary))
    }
    if (parentAgingTitle) {
      const newRow: ExcelRow = {}
      newRow[kind === ReportKind.APAging ? VENDOR_NAME : CUSTOMER_NAME] = {
        v: parentAgingTitle.title,
        t: "s",
      }
      agg.push(newRow)
    }
    if (parentAgingSummary) {
      agg.push(mapParentTitleSummaryRowColumns(kind, parentAgingSummary))
    }
    if (agingSummary) {
      const blankRow: ExcelRow = {}
      blankRow["Current"] = {
        v: "",
        t: "s",
      }
      agg.push(blankRow)
      const summaryRow = mapParentTitleSummaryRowColumns(kind, agingSummary)
      summaryRow[kind === ReportKind.APAging ? "Vendor Name" : "Customer Name"] = {
        v: "",
        t: "s",
      }
      summaryRow["Invoice Date"] = {
        v: "TOTAL",
        t: "s",
      }
      agg.push(summaryRow)
    }
    return agg
  }, [] as ExcelRow[])
}

function mapParentTitleSummaryRowColumns(
  kind: ReportKind.APAging | ReportKind.ARAging,
  rowSummary: AgingSummary
): ExcelRow {
  const row: ExcelRow = {}
  const columns = kind === ReportKind.APAging ? AP_TITLE_SUMMARY_COLUMNS : AR_TITLE_SUMMARY_COLUMNS
  columns.forEach((column) => {
    let idx: number
    let value: MonetaryValue | undefined
    switch (column) {
      case "Vendor Name":
      case "Customer Name":
        row[column] = {
          v: rowSummary.label,
          t: "s",
        }
        break
      case "Current":
      case "1-30 Days":
      case "31-60 Days":
      case "61-90 Days":
      case "Older":
        idx = TIME_COLUMNS.indexOf(column)
        value = rowSummary.dimensionSummary.aging[idx]?.summary.total.current?.total.value
        row[column] = value?.amount
          ? {
              v: Math.abs(monetaryValueHelper.unmultipliedAmount(value)),
              t: "n",
              z: "#,##0.00_)",
            }
          : {
              v: "",
              t: "s",
            }
        break
      case "Total":
        value = rowSummary.dimensionSummary.summary.total.current?.total?.value
        row[column] = value?.amount
          ? {
              v: Math.abs(monetaryValueHelper.unmultipliedAmount(value)),
              t: "n",
              z: "#,##0.00_)",
            }
          : {
              v: "",
              t: "s",
            }
    }
  })

  return row
}

const columnMapping = new Map<
  (typeof TIME_COLUMNS)[number],
  (typeof LEAF_AGING_AMOUNT_COLUMNS)[number]
>([
  ["Current", LeafAgingSummaryAmountColumn.Current],
  ["1-30 Days", LeafAgingSummaryAmountColumn.OneToThirty],
  ["31-60 Days", LeafAgingSummaryAmountColumn.ThirtyOneToSixty],
  ["61-90 Days", LeafAgingSummaryAmountColumn.SixtyOneToNinety],
  ["Older", LeafAgingSummaryAmountColumn.Older],
])

function mapInvoiceSummaryColumns(
  kind: ReportKind.APAging | ReportKind.ARAging,
  rowSummary: LeafAgingSummary
): ExcelRow {
  const row: ExcelRow = {}
  const columns = kind === ReportKind.APAging ? AP_SUMMARY_COLUMNS : AR_SUMMARY_COLUMNS

  columns.forEach((column) => {
    let value: MonetaryValue | undefined
    switch (column) {
      case "Customer Name":
      case "Vendor Name":
        row[column] = {
          v: "",
          t: "s",
        }
        break
      case "Invoice Date":
        row[column] = {
          v: dayjs.unix(rowSummary.invoiceDate).utc().format("MM/DD/YYYY"),
          t: "s",
        }
        break
      case "Due Date":
        row[column] = {
          v: dayjs.unix(rowSummary.dueDate).utc().format("MM/DD/YYYY"),
          t: "s",
        }
        break
      case "Invoice Number":
        row[column] = {
          v: rowSummary.referenceNumber ?? "",
          t: "s",
        }
        break
      case "Current":
      case "1-30 Days":
      case "31-60 Days":
      case "61-90 Days":
      case "Older":
        value = rowSummary.amounts.find((amount) => amount.column === columnMapping.get(column))
          ?.moneyFlow.value
        row[column] = value?.amount
          ? {
              v: Math.abs(monetaryValueHelper.unmultipliedAmount(value)),
              t: "n",
              z: "#,##0.00_)",
            }
          : {
              v: "",
              t: "s",
            }
        break
      case "Total":
        value = rowSummary.amounts[0]?.moneyFlow.value
        row[column] = value?.amount
          ? {
              v: Math.abs(monetaryValueHelper.unmultipliedAmount(value)),
              t: "n",
              z: "#,##0.00_)",
            }
          : {
              v: "",
              t: "s",
            }
    }
  })

  return row
}
